import React from "react";
import Footer from "../FooterBn";

/**
 * Goat Teachers — Privacy Policy
 * Tailwind-only (no CSS), matching the same UI style as your Guidelines/About/Terms pages.
 */

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    {children}
  </div>
);

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-3">
      <h2 className="text-lg font-black text-slate-900 sm:text-xl">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {subtitle}
        </p>
      ) : null}
    </div>
    <Card>{children}</Card>
  </section>
);

const RuleList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
        <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-slate-300" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const InfoBox = ({ title, children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-rose-200 bg-rose-50 text-rose-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div
      className={`rounded-2xl border p-4 text-sm leading-relaxed ${tones[tone]}`}
    >
      <div className="mb-1 font-extrabold">{title}</div>
      <div>{children}</div>
    </div>
  );
};

const PrivacyPolicy = () => {
  const effectiveDate = "20-12-2025";
  const lastUpdated = "20-12-2025";

  const navItems = [
    ["collect", "1. Information We Collect"],
    ["use", "2. How We Use Your Information"],
    ["sharing", "3. Sharing of Information"],
    ["ugc", "4. User-Generated Content"],
    ["cookies", "5. Cookies and Tracking Technologies"],
    ["retention", "6. Data Retention"],
    ["security", "7. Data Security"],
    ["rights", "8. User Rights"],
    ["children", "9. Children’s Privacy"],
    ["thirdparty", "10. Third-Party Links"],
    ["transfers", "11. International Data Transfers"],
    ["changes", "12. Changes to This Privacy Policy"],
    ["law", "13. Governing Law"],
    ["contact", "14. Contact Information"],
  ];

  return (
    <>
      <div className="relative isolate bg-slate-50">
        {/* subtle background blobs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute right-[-120px] top-[180px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute left-[-120px] top-[420px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Chip>Legal</Chip>
                  <Chip>Privacy</Chip>
                  <Chip>Data Protection</Chip>
                </div>

                <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  Goat Teachers – Privacy Policy
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  Goat Teachers (“we,” “us,” or “our”) is committed to
                  protecting your privacy. This Privacy Policy explains how we
                  collect, use, store, disclose, and protect personal
                  information when you access or use the Goat Teachers website,
                  mobile applications, and related services (collectively, the
                  “Platform”).
                  <br />
                  By using the Platform, you consent to the practices described
                  in this Privacy Policy.
                </p>

                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Effective Date:{" "}
                    <span className="font-bold text-slate-900">
                      {effectiveDate}
                    </span>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Last Updated:{" "}
                    <span className="font-bold text-slate-900">
                      {lastUpdated}
                    </span>
                  </span>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="w-full lg:max-w-xs">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Quick Navigation
                  </div>
                  <div className="mt-3 grid gap-2 text-sm">
                    {navItems.map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 shadow-sm hover:border-slate-300"
                      >
                        <span className="font-semibold">{label}</span>
                        <span className="text-slate-400 group-hover:text-slate-700">
                          →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <InfoBox
                title="We do not sell personal information"
                tone="success"
              >
                Goat Teachers does not sell or rent personal information to
                third parties.
              </InfoBox>
            </div>
          </div>

          {/* Sections */}
          <div className="grid gap-6">
            <Section
              id="collect"
              title="1. Information We Collect"
              subtitle="We collect information to operate the Platform and provide services."
            >
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    1.1 Personal Information
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "Full name",
                        "Email address",
                        "Phone number",
                        "Profile information (education, institution, experience)",
                        "Account login details",
                      ]}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    1.2 Usage Information
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "IP address",
                        "Device type and browser",
                        "Pages visited and actions taken",
                        "Date and time of access",
                      ]}
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    1.3 Transaction Information
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "Credit purchases and usage history",
                        "Payment transaction status (processed via third-party providers)",
                      ]}
                    />
                  </div>
                  <div className="mt-3">
                    <InfoBox title="Payment Security" tone="neutral">
                      Goat Teachers does not store sensitive payment credentials
                      such as PINs or card details.
                    </InfoBox>
                  </div>
                </div>
              </div>
            </Section>

            <Section id="use" title="2. How We Use Your Information">
              <RuleList
                items={[
                  "Create and manage user accounts",
                  "Enable tutor-student connections",
                  "Process payments and credits",
                  "Improve platform performance and user experience",
                  "Communicate updates, alerts, and support messages",
                  "Prevent fraud, abuse, or misuse",
                  "Comply with legal and regulatory requirements",
                ]}
              />
            </Section>

            <Section id="sharing" title="3. Sharing of Information">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>We may share information:</p>
                <RuleList
                  items={[
                    "With service providers (payment gateways, hosting, analytics) solely for operational purposes",
                    "When required by law, court order, or government authority",
                    "To protect the rights, safety, and integrity of Goat Teachers and its users",
                  ]}
                />
                <InfoBox title="No selling of data" tone="success">
                  We do not sell or rent personal information to third parties.
                </InfoBox>
              </div>
            </Section>

            <Section id="ugc" title="4. User-Generated Content">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  Any information you voluntarily post publicly (such as
                  reviews, ratings, or profile content) may be visible to other
                  users.
                </p>
                <InfoBox title="Public Information" tone="warn">
                  You acknowledge that Goat Teachers is not responsible for how
                  other users use publicly available information.
                </InfoBox>
              </div>
            </Section>

            <Section id="cookies" title="5. Cookies and Tracking Technologies">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>We may use cookies and similar technologies to:</p>
                <RuleList
                  items={[
                    "Maintain login sessions",
                    "Analyze usage trends",
                    "Improve platform functionality",
                  ]}
                />
                <InfoBox title="Cookie Controls" tone="neutral">
                  You may disable cookies through your browser settings;
                  however, some features may not function properly.
                </InfoBox>
              </div>
            </Section>

            <Section id="retention" title="6. Data Retention">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>We retain personal information only for as long as:</p>
                <RuleList
                  items={[
                    "Necessary to provide services",
                    "Required to comply with legal obligations",
                    "Needed to resolve disputes or enforce agreements",
                  ]}
                />
                <InfoBox title="Aggregated Data" tone="neutral">
                  We may retain anonymized or aggregated data indefinitely.
                </InfoBox>
              </div>
            </Section>

            <Section id="security" title="7. Data Security">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  We implement reasonable administrative, technical, and
                  organizational measures to protect personal data.
                </p>
                <InfoBox title="No system is perfect" tone="warn">
                  However, no system is completely secure, and we cannot
                  guarantee absolute data security.
                </InfoBox>
              </div>
            </Section>

            <Section id="rights" title="8. User Rights">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>Subject to applicable laws, you may:</p>
                <RuleList
                  items={[
                    "Access your personal information",
                    "Request correction of inaccurate data",
                    "Request deletion of your account and associated data",
                  ]}
                />
                <InfoBox title="How to request" tone="neutral">
                  Requests may be made by contacting us at{" "}
                  <a
                    href="mailto:support@goatteachers.com"
                    className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                  >
                    support@goatteachers.com
                  </a>
                  .
                </InfoBox>
              </div>
            </Section>

            <Section id="children" title="9. Children’s Privacy">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  The Platform is not intended for children under the age of 13.
                </p>
                <p>
                  We do not knowingly collect personal data from children
                  without parental consent.
                </p>
              </div>
            </Section>

            <Section id="thirdparty" title="10. Third-Party Links">
              <div className="text-sm leading-relaxed text-slate-700">
                The Platform may contain links to third-party websites or
                services. Goat Teachers is not responsible for the privacy
                practices or content of such third parties.
              </div>
            </Section>

            <Section id="transfers" title="11. International Data Transfers">
              <div className="text-sm leading-relaxed text-slate-700">
                By using the Platform, you acknowledge that your data may be
                stored or processed on servers located outside your country,
                subject to appropriate safeguards.
              </div>
            </Section>

            <Section id="changes" title="12. Changes to This Privacy Policy">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  We reserve the right to update this Privacy Policy at any
                  time.
                </p>
                <p>
                  Changes will be effective upon posting on the Platform.
                  Continued use of the Platform constitutes acceptance of the
                  updated Privacy Policy.
                </p>
              </div>
            </Section>

            <Section id="law" title="13. Governing Law">
              <div className="text-sm leading-relaxed text-slate-700">
                This Privacy Policy shall be governed by and interpreted in
                accordance with the laws of the People’s Republic of Bangladesh.
              </div>
            </Section>

            <Section id="contact" title="14. Contact Information">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>
                  For privacy-related inquiries or requests, please contact:
                </p>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Goat Teachers
                  </div>
                  <div className="mt-2 space-y-1">
                    <div>
                      Email:{" "}
                      <a
                        href="mailto:support@goatteachers.com"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        support@goatteachers.com
                      </a>
                    </div>
                    <div>
                      Website:{" "}
                      <a
                        href="https://www.goatteachers.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        https://www.goatteachers.com
                      </a>
                    </div>
                  </div>
                </div>

                <InfoBox title="Tip" tone="neutral">
                  For faster help, include your account email/phone (if
                  applicable) and clearly mention “Privacy Request” in the
                  subject line.
                </InfoBox>
              </div>
            </Section>

            {/* Bottom reminder */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
              <div className="font-extrabold text-slate-900">Reminder</div>
              <p className="mt-1 leading-relaxed">
                By continuing to use Goat Teachers, you acknowledge that you
                have read and agree to this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;
